/**
 * QuestionCard -- Display card for a single checklist question
 *
 * Phase 2: Read-only display with collapsed mode only.
 * Phase 3: Manual answer form with save/edit/clear flow.
 *
 * State machine:
 * - "collapsed" -- default display (answered or unanswered)
 * - "answering" -- manual answer form is shown
 * - "ai_loading" (phase 4) -- AI generation in progress
 * - "ai_preview" (phase 4) -- AI answer preview
 * - "no_evidence" (phase 4) -- no documents linked
 */

import { useEffect, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  Sparkles,
  Trash2,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import type {
  GeneratedQuestion,
  ChecklistItemAnswer,
  QuestionType,
  UIChecklistItem,
  ChecklistItemId,
} from "@/packages/types";
import {
  useSaveChecklistAnswer,
  useClearChecklistAnswer,
  useGenerateAnswerFromEvidence,
} from "@/hooks/use-checklist-answers";
import { toast } from "sonner";
import { AnswerDisplay } from "./AnswerDisplay";
import { AnswerForm } from "./AnswerForm";

// ─── Props ──────────────────────────────────────────────────────────────────

interface QuestionCardProps {
  question: GeneratedQuestion;
  existingAnswer?: ChecklistItemAnswer;
  checklistItemId: ChecklistItemId;
  onSwitchToEvidence: () => void;
  item: UIChecklistItem;
}

// ─── Mode State Machine ─────────────────────────────────────────────────────

type QuestionCardMode =
  | "collapsed"
  | "answering"
  | "ai_loading"
  | "no_evidence";

// ─── Type Badge ──────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<QuestionType, string> = {
  yes_no: "YES/NO",
  date: "DATE",
  document: "DOCUMENT",
  text: "TEXT",
  selection: "SELECTION",
};

function TypeBadge({ type }: { type: QuestionType }) {
  return (
    <span className="inline-flex items-center rounded bg-muted/15 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-muted uppercase">
      {TYPE_LABELS[type]}
    </span>
  );
}

// ─── Required Badge ──────────────────────────────────────────────────────────

function RequiredBadge({ required }: { required: boolean }) {
  if (required) {
    return (
      <span className="inline-flex items-center rounded bg-red-500/10 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-red-600 uppercase">
        Required
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded bg-muted/10 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-muted uppercase">
      Optional
    </span>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function QuestionCard({
  question,
  existingAnswer,
  checklistItemId,
  onSwitchToEvidence,
  item,
}: QuestionCardProps) {
  const [mode, setMode] = useState<QuestionCardMode>("collapsed");
  const [pendingEditValue, setPendingEditValue] = useState<
    string | boolean | number | undefined
  >(undefined);
  const isAnswered = !!existingAnswer;

  const saveAnswer = useSaveChecklistAnswer();
  const clearAnswer = useClearChecklistAnswer();
  const generateAnswer = useGenerateAnswerFromEvidence();

  // When the real-time answer arrives, exit ai_loading
  useEffect(() => {
    if (mode === "ai_loading" && existingAnswer) {
      setMode("collapsed");
    }
  }, [mode, existingAnswer]);

  async function handleSave(value: string | boolean | number) {
    await saveAnswer.mutateAsync({
      checklistItemId,
      answer: {
        question: {
          text: question.text,
          type: question.type,
          required: question.required,
          hint: question.hint,
          expectedEvidence: question.expectedEvidence,
          order: question.order,
        },
        answer: value,
        source: "manual" as const,
      },
    });
    setMode("collapsed");
  }

  async function handleClear() {
    await clearAnswer.mutateAsync({
      checklistItemId,
      questionOrder: question.order,
    });
    setMode("collapsed");
  }

  async function handleGenerate() {
    setMode("ai_loading");
    try {
      const result = await generateAnswer.mutateAsync({
        checklistItemId,
        questionOrder: question.order,
      });

      if (!result?.hasEvidence) {
        setMode("no_evidence");
        return;
      }
      // Stay in ai_loading — useEffect transitions to collapsed when answer arrives via real-time update
    } catch {
      toast.error(
        "Could not generate answer. Please try again or answer manually.",
      );
      setMode("collapsed");
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card px-3.5 py-3 space-y-2.5">
      {/* Question header */}
      <div className="flex items-start gap-2">
        {/* Answered checkmark */}
        {isAnswered && (
          <CheckCircle2 size={14} className="text-green-500 shrink-0 mt-0.5" />
        )}

        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Meta line: Q{order} + type badge + required */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-semibold text-secondary">
              Q{question.order}
            </span>
            <span className="text-[10px] text-muted">&middot;</span>
            <TypeBadge type={question.type} />
            <span className="text-[10px] text-muted">&middot;</span>
            <RequiredBadge required={question.required} />
          </div>

          {/* Question text */}
          <p className="text-[13px] text-primary leading-relaxed">
            {question.text}
          </p>

          {/* Hint (if present, only shown in collapsed mode) */}
          {mode === "collapsed" && question.hint && (
            <p className="text-[11px] text-muted italic">{question.hint}</p>
          )}
        </div>
      </div>

      {/* Answering mode: show the form */}
      {mode === "answering" && (
        <AnswerForm
          question={question}
          initialValue={pendingEditValue ?? existingAnswer?.answer}
          onSave={(value) => {
            setPendingEditValue(undefined);
            return handleSave(value);
          }}
          onCancel={() => {
            setPendingEditValue(undefined);
            setMode("collapsed");
          }}
          isSaving={saveAnswer.isPending}
        />
      )}

      {/* AI Loading mode */}
      {mode === "ai_loading" && (
        <div className="flex items-center gap-2 py-2 text-[12px] text-accent">
          <Loader2 size={13} className="animate-spin" />
          Analyzing {item.linkedDocumentIds?.length ?? 0} linked documents…
        </div>
      )}

      {/* No Evidence mode */}
      {mode === "no_evidence" && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/5 p-3 space-y-1.5">
          <p className="text-[12px] font-medium text-amber-700 flex items-center gap-1.5">
            <AlertTriangle size={13} /> No linked documents
          </p>
          <p className="text-[12px] text-secondary">
            Link documents in the Evidence tab first, then try generating an
            answer.
          </p>
          <button
            type="button"
            onClick={onSwitchToEvidence}
            className="text-[11px] text-accent hover:underline"
          >
            Go to Evidence →
          </button>
        </div>
      )}

      {/* Collapsed mode: show answer or action buttons */}
      {mode === "collapsed" &&
        (isAnswered ? (
          <div className="space-y-2">
            <AnswerDisplay
              answer={existingAnswer}
              onEdit={() => setMode("answering")}
            />
            {/* Clear answer button */}
            <button
              type="button"
              onClick={handleClear}
              disabled={clearAnswer.isPending}
              className="inline-flex items-center gap-1 text-[11px] text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              <Trash2 size={10} />
              {clearAnswer.isPending ? "Clearing..." : "Clear answer"}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 pt-0.5">
            <button
              type="button"
              onClick={() => setMode("answering")}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-[12px] font-medium text-secondary hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
            >
              Answer
              <ChevronDown size={12} />
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generateAnswer.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-accent/20 bg-accent/5 px-2.5 py-1.5 text-[12px] font-medium text-accent hover:bg-accent/10 transition-colors cursor-pointer disabled:opacity-50"
            >
              <Sparkles size={12} />
              Generate
            </button>
          </div>
        ))}
    </div>
  );
}
